import HttpClient from "../../services/HttpClient";
import {getRoot, resolveIdentifier} from "mobx-state-tree";
import DataExportTypeStore from "../../stores/domain/DataExportTypeStore";
import {deliveryTypes} from "../../utils/constants";
import lodash from "lodash";
import moment from "moment-timezone";

class DataExportManager {

  constructor(httpClient = null) {
    this.httpClient = httpClient || new HttpClient();
    this.dataExportURL = `/data/exports`;
    this.dataExportTypeURL = `/data/export-types`;
    this.dataDeliveryTypeUrl = '/data/delivery-types'
    this.dataExportFiltersURL = '/sites/filters/data-export';
  }

  handleError(instance, error) {
    instance?.handleHasErrors(true);
    instance?.handleErrorMessage(error?.message);
    console.error(`Error: ${error?.message}`, error);
    return error;
  }

  cleanUpDeliveryInfo(instance) {
    if(!instance.isDeliveryOpened && !lodash.isEmpty(instance?.getDeliveryFormData())) {
      return {}
    } else if(!instance.isDeliveryOpened && lodash.isEmpty(instance?.getDeliveryFormData())) {
      return {}
    }
    else {
      return {
        "type": instance?.dataDeliveryTypeSelected?.type,
        "deliveryData": instance.getDeliveryFormData(),
        "hasDeliveryInfo": instance?.isDeliveryOpened,
        "createdBy": instance?.dataDeliveryTypeSelected?.type === deliveryTypes.EMAIL ? instance?.userEmail : ""
      }
    }
  }

  createRequest(instance) {
    const groups = instance?.groupSelectStore?.selectedGroups?.flatMap((group) => group?.uuid);

    // Build data-export data

    let exportData = instance?.getFormData();
    const exportScheduleData = instance?.getExportScheduleFormData();
    switch (instance?.dataExportTypeSelected?.type) {
      case "scanCount":
        const dataExportTypeStore = resolveIdentifier(DataExportTypeStore, getRoot(instance), 'dets');
        const exportSchema = dataExportTypeStore.dataExportTypes.find(e => e.type === "scanCount");

        const shouldAddTotalRow = true;
        const exportUISchema = JSON.parse(exportSchema.uiSchema);

        const allScansColumns = exportUISchema?.columns?.["ui:options"]?.columnOptions?.filter(op => !["Time Period", "Count Functions"].includes(op.label))?.map(colOptions => colOptions?.options)?.flat();
        const allCountColumns = exportUISchema?.columns?.["ui:options"]?.columnOptions?.find(op => op.label === "Count Functions")?.options;
        const allTimeColumns = exportUISchema?.columns?.["ui:options"]?.columnOptions?.find(op => op.label === "Time Period")?.options;

        const itemExportSchema = dataExportTypeStore.dataExportTypes.find(e => e.type === "default");
        const itemExportUISchema = JSON.parse(itemExportSchema.uiSchema);
        const onlyQRColumns = itemExportUISchema?.columns?.["ui:options"]?.columnOptions?.find(op => op.label === "QR Codes")?.options;

        const scansExportSchema = dataExportTypeStore.dataExportTypes.find(e => e.type === "scans");
        const scansExportUISchema = JSON.parse(scansExportSchema.uiSchema);
        const onlyScanColumns = scansExportUISchema?.columns?.["ui:options"]?.columnOptions?.find(op => op.label === "Scans")?.options;


        const splitColumnName = (col) => {
          const [name, alias] = col?.split(" as ") ?? [];
          return { name, alias: alias?.replaceAll('"', '').trim() };
        };

        const getEquivalentScanColumn = (column) => {
          return onlyScanColumns?.find(col => col.label === splitColumnName(column).alias);
        };

        const unionSelectColumns = [];
        // Group By based on the columns selected
        exportData.groupBy = [];
        const unionGroupColumns = [];
        exportData.columns?.forEach(column => {
          let currentCol = column;
          const [columnName, columnAlias] = column?.split(" as ");

          if (allScansColumns?.map(col => col.value)?.includes(column)) {
            // Scan Columns
            exportData.groupBy.push(columnName);

            // Replace 'code' columns with 'scans' columns for v1 scan count
            if (onlyQRColumns?.map(col => col.value)?.includes(column)) {
              const matchedScanCol = getEquivalentScanColumn(column);

              if (matchedScanCol) {
                currentCol = matchedScanCol.value;
                unionGroupColumns.push(splitColumnName(matchedScanCol.value).name);
              } else {
                currentCol = columnAlias
                  ? `'V1 Code - ${columnAlias}' as ${columnAlias}`
                  : `'V1 Code'`
              }
            }
          } else if (allTimeColumns?.map(col => col.value)?.includes(column)) {
            // Time Period Columns
            const selectedTimeColumn = allTimeColumns?.find(col => col.value === column);
            // Use ROLLUP() when applying GROUPING and CASE
            if (selectedTimeColumn.value.includes('CASE') || selectedTimeColumn.value.includes('GROUPING')) {
              const groupByCol = shouldAddTotalRow ? `ROLLUP(${selectedTimeColumn.name})` : selectedTimeColumn.name;
              exportData.groupBy.push(groupByCol);
              unionGroupColumns.push(groupByCol);
            }
          }
          unionSelectColumns.push(currentCol);
        });

        // Add V1 scan count as a single aggregated total row
        if (instance?.v1ScanCountSetting) {
          const unionQueryDef = lodash.cloneDeep(exportData);
          // Remove unnecessary properties from union-query
          delete unionQueryDef.setOperation;
          delete unionQueryDef.transformations;

          // The union-query should include only the 'scans' column(s) to avoid an unnecessary join with the 'code' table
          unionQueryDef.columns = unionSelectColumns;
          unionQueryDef.groupBy = unionGroupColumns;

          // Add additional filters to union-query
          if (exportScheduleData?.additionalFilters?.toggle) {
            this.addAdditionalFilters(unionQueryDef, exportScheduleData.additionalFilters.definition);
          }

          // Add V1 code filter to union-query
          const scanResourceName = (allScansColumns?.find(col => col.label === "Scan Timestamp")?.value)?.split(".")[0];
          const scanResourceQRColumnName = allCountColumns?.find(col => col.label === "Unique Code Count")?.name;

          this.addAdditionalFilters(unionQueryDef, { onColumns: [{
              "name": `${scanResourceName}.${scanResourceQRColumnName}`,
              "condition": "not in",
              "value": {
                "columns": [
                  allScansColumns?.find(col => col.label === "Code UUID")?.name
                ]
              }
            }]
          });

          // Check and replace 'code' columns with 'scans' columns
          const findCodeFilterAndReplace = (filters) => {
            if (!filters?.length) return;
            for (let i = filters.length - 1; i >= 0; i--) {
              const col = filters[i];
              const qrCol = onlyQRColumns?.find(c => splitColumnName(c.value).name === col.name);
              if (qrCol) {
                const matchedScanCol = getEquivalentScanColumn(qrCol.value);
                if (matchedScanCol) {
                  col.name = splitColumnName(matchedScanCol.value).name;
                } else {
                  console.warn(`Warn: UNION query could not find filter for column: '${qrCol.value}'`);
                  filters.splice(i, 1); // remove filter in-place
                }
              }
            }
          };

          findCodeFilterAndReplace(unionQueryDef.filter.definition?.onColumns);
          findCodeFilterAndReplace(unionQueryDef.filter.definition?.betweenFilters);

          // To union V1 Scan Count, it should have the same number of SELECT columns
          if (unionQueryDef.columns.length !== exportData.columns.length) {
            console.error(`Error: UNION query should have same number of columns`);
            break;
          }

          exportData.setOperation = {
            type: "UNION",
            definition: unionQueryDef
          };
        } else {
          // Clear definition
          exportData.setOperation = {};
        }
        break;
    }

    // Check and add default Code Status filters for report
    if (instance?.isCodeStatusExcludedByDefault) {
      try {
        // Fetch 'Status' column name
        const qrStatusColumn = instance?.getStatusColumn();

        const statusColName = qrStatusColumn.value;

        // Fetch column names which have filters
        const filtersOnStatusColumnByUser = exportData?.filter?.definition?.onColumns?.filter(f => f.name === statusColName) || [];
        const filtersOnStatusByUser = filtersOnStatusColumnByUser?.map(c => c.value);

        for (const status of instance?.defaultExcludedStatus) {
          // Check if (status column name exists & is a valid status & we don't have a filter on the same status)
          if (statusColName && qrStatusColumn.inputSchema.enum.includes(status) && !filtersOnStatusByUser.includes(status)) {
            exportData = this.addAdditionalFilters(exportData, {
              onColumns: [{
                name: statusColName,
                condition: "!=",
                value: status,
                useOr: false
              }]
            });
          }
        }
      } catch (e) {
        console.error("DataExportManager Error: Failed to add default filter on status");
        console.error(e);
      }
    }

    if (!instance?.isTimeZoneDefault()) {
      instance?.convertTimeInExportDefinition({
        "definition": exportData,
        "schedule": exportScheduleData,
      }, instance?.timezone, "UTC");
    }

    const payload = {
      "name": instance?.name,
      "definition": exportData,
      "schedule": exportScheduleData,
      "type": instance?.dataExportTypeSelected?.type,
      "tags": instance?.tagSelectStore?.selectedTags?.flatMap((tag) => tag?.uuid),
      "format": instance?.format,
      "timezone": instance?.timezone,
      "isExclusivelyGroupOwned": instance?.isExclusivelyGroupOwned,
      "owner": {
        ...(groups?.length && {"groupUuid": groups[0]}),
        ...(groups?.length && {"groups": groups}),
        ...(instance?.dimensionsMultiSelectStore?.dimensionStore?.dimensionsEnabled &&
          { "dimensions": instance?.getSelectedDimensions() })
      },
      "deliveryInfo": this.cleanUpDeliveryInfo(instance),
      "hideActions": instance?.hideActions
    };
    console.log(payload);
    return payload;
  }

  addAdditionalFilters(definition={}, filters) {
    const {onColumns, betweenFilters} = filters;

    if (definition.filter?.toggle) {
      if (onColumns?.length) {
        definition.filter.definition.onColumns = definition.filter.definition.onColumns?.length ?
          definition.filter.definition.onColumns.concat(onColumns) :
          onColumns;
      }

      if (betweenFilters?.length) {
        definition.filter.definition.betweenFilters = definition.filter.definition.betweenFilters?.length ?
          definition.filter.definition.betweenFilters.concat(betweenFilters) :
          betweenFilters;
      }
    } else {
      if (onColumns?.length || betweenFilters?.length) {
        definition.filter = {
          toggle: true,
          definition: {
            onColumns,
            betweenFilters
          }
        };
      }
    }

    return definition;
  }

  async fetchAllForMe(queryParams, instance) {
    let response;
    try {
      response = await this.httpClient.get(`/me${this.dataExportURL}`, queryParams);
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }

  async fetchByUUID(uuid, queryParams, instance) {
    let response;
    try {
      response = await this.httpClient.get(`${this.dataExportURL}/${uuid}`, queryParams);
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }

  async fetchAllTypes(queryParams, instance) {
    let response;
    try {
      response = await this.httpClient.get(this.dataExportTypeURL, queryParams);
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }

  async fetchAllDeliveryTypes(queryParams, instance) {
    let response;
    try {
      response = await this.httpClient.get(this.dataDeliveryTypeUrl, queryParams);
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }

  async fetchAllFilters(queryParams, instance) {
    let response;
    try {
      response = await this.httpClient.get(this.dataExportFiltersURL, queryParams);;
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }

  async getDownloadLink(uuid, instance) {
    let response;
    try {
      response = await this.httpClient.get(`${this.dataExportURL}/${uuid}/download-link`);
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }

  async performAction(uuid, action, instance) {
    let response;
    try {
      response = await this.httpClient.get(`${this.dataExportURL}/${uuid}/actions/${action}`);
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }
  async delete(uuid, instance) {
    let response;
    try {
      response = await this.httpClient.destroy(`${this.dataExportURL}/${uuid}`);
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }
  async save(instance) {
    let response;
    try {
      response = await this.httpClient.post(this.dataExportURL, this.createRequest(instance));
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }

  async update(uuid, instance) {
    let response;
    try {
      response = await this.httpClient.put(`${this.dataExportURL}/${uuid}`, this.createRequest(instance));
    } catch (e) {
      this.handleError(instance, e);
    }
    return response;
  }
}

export default DataExportManager;
